import type { SupplyIntelConfig } from "./config";
import { SupplyIntelGradeStatus } from "./next-step";

const SHOPIFY_TAXONOMY_GID_PREFIX = "gid://shopify/TaxonomyCategory/";
/** Shopify's "Uncategorized" node, which says nothing about the product. */
const SHOPIFY_UNCATEGORIZED_ID = "na";

/** How a category is resold. */
export enum SupplyIntelCategoryRule {
  /** Never resold, such as intimates. */
  Excluded = "excluded",
  /** Resold only by grades the brand sells as new. */
  NewOnly = "new_only",
}

export interface SupplyIntelCategoryRuleEntry {
  /** Names the category in the next step's reason. */
  label: string;
  rule: SupplyIntelCategoryRule;
}

/** A product family's category from the Shopify standard product taxonomy. */
export interface SupplyIntelCategory {
  /** The full path, such as "Apparel & Accessories > Clothing > Swimwear". */
  name: string;
  /** Such as "aa-1-20"; null for a category from another taxonomy. */
  shopifyId: string | null;
}

/** A `product_taxonomy` row as a category; null for Shopify's "Uncategorized". */
export function supplyIntelCategory({
  name,
  source,
  sourceId,
}: {
  name: string;
  source: string;
  sourceId: string;
}): SupplyIntelCategory | null {
  const shopifyId =
    source === "shopify" && sourceId.startsWith(SHOPIFY_TAXONOMY_GID_PREFIX)
      ? sourceId.slice(SHOPIFY_TAXONOMY_GID_PREFIX.length)
      : null;
  return shopifyId === SHOPIFY_UNCATEGORIZED_ID ? null : { name, shopifyId };
}

/**
 * The rule for the most specific of `shopifyId` and its ancestors that the
 * config lists, so a narrower entry can refine a broader one. Taxonomy IDs
 * nest by suffix: "aa-1-20-24" is under "aa-1-20".
 */
function categoryRule(
  shopifyId: string,
  rules: SupplyIntelConfig["categoryRules"],
): SupplyIntelCategoryRuleEntry | undefined {
  const parts = shopifyId.split("-");
  for (let length = parts.length; length > 0; length--) {
    const entry = rules[parts.slice(0, length).join("-")];
    if (entry) {
      return entry;
    }
  }
  return undefined;
}

/**
 * Why units of this category and grade aren't resold, or null when they can
 * be. A new-only category excludes grades the brand doesn't sell as new;
 * other grades go through the usual grade rules.
 */
export function supplyIntelCategoryExclusion(
  category: SupplyIntelCategory | null,
  gradeStatus: SupplyIntelGradeStatus,
  config: Pick<SupplyIntelConfig, "categoryRules">,
): string | null {
  if (category?.shopifyId == null) {
    return null;
  }
  const entry = categoryRule(category.shopifyId, config.categoryRules);
  switch (entry?.rule) {
    case SupplyIntelCategoryRule.Excluded:
      return `Category not resold: ${entry.label}`;
    case SupplyIntelCategoryRule.NewOnly:
      return gradeStatus === SupplyIntelGradeStatus.NotSoldAsNew
        ? `Category resold only as new: ${entry.label}`
        : null;
    case undefined:
      return null;
  }
}

/** The category path without its top level, such as "Clothing > Swimwear". */
export function supplyIntelCategoryLabel(name: string): string {
  const parts = name.split(" > ");
  return parts.length > 1 ? parts.slice(1).join(" > ") : name;
}
